import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { ref, computed, readonly } from 'vue';
import {
  SEO_MODULE_GLOBAL,
  SEO_MODULE_PRODUCT,
  SEO_MODULE_CATEGORY,
  SEO_MODULE_CMS,
} from '~~/common/config/SeoConfig.ts';
import { SeoTitle } from '~~/common/utils/SeoTitle.ts';

export const useSeoStore = defineStore('seo', () => {
  const nuxtApp = useNuxtApp();
  const _seoRequestsEnRoute = ref([]);
  const _data = ref({});
  const _titlePartPrefix = ref('');
  const _titlePartMain = ref('');
  const _titlePartPostfix = ref('');
  const _responseCache = ref({});
  const _promiseCache = ref({});
  function removeSeoRequestsEnRoute (payload) {
    const i = _seoRequestsEnRoute.value.indexOf(payload);
    if (i !== -1) {
      _seoRequestsEnRoute.value.splice(i, 1);
    }
  }

  function createSeoTitleFromStore () {
    return new SeoTitle(
      _titlePartPrefix.value,
      _titlePartMain.value,
      _titlePartPostfix.value,
    );
  }

  function _setSeoData (payload) {
    _titlePartPrefix.value = '';
    _titlePartMain.value = typeof payload?.title === 'string' ? payload.title : '';
    _titlePartPostfix.value = typeof payload?.titlePostfix === 'string' ? payload.titlePostfix : '';
    _data.value = _computeSeoHeads({
      computedTitle: createSeoTitleFromStore().joinedTitle(),
      seo: payload || {},
    });
  }

  async function getCareerPageTitle (payload) {
    const { $awt } = nuxtApp;
    return new SeoTitle(
      payload?.pageTitle || '',
      `${payload?.pageTitle ? ' - ' : ''}${$awt('aw.career.seo.title')}`,
      (await getSeoData({ seoModule: SEO_MODULE_GLOBAL, id: -1 }))?.titlePostfix,
    ).joinedTitle();
  }

  async function fetchSeoData (payload) {
    const { $logger } = nuxtApp;
    let seoReq;
    try {
      seoReq = getSeoData(payload);
      _seoRequestsEnRoute.value.push({ ...payload, seoModule: getSeoModuleString(payload.seoModule) });
      _setSeoData({ title: 'Auchan', ...await seoReq });
    } catch (error) {
      _setSeoData({ title: 'Auchan' });
      $logger.error(error);
    } finally {
      removeSeoRequestsEnRoute(seoReq);
    }
  }

  function getSeoModuleString (seoModule) {
    return {
      [SEO_MODULE_GLOBAL]: 'global',
      [SEO_MODULE_PRODUCT]: 'productSeoResource',
      [SEO_MODULE_CATEGORY]: 'categorySeoResource',
      [SEO_MODULE_CMS]: 'cms_content',
    }[seoModule];
  }

  async function getSeoData ({ seoModule, id } = { seoModule: SEO_MODULE_GLOBAL, id: -1 }) {
    const { $logger, $api, $i18n } = nuxtApp;
    try {
      const seoModuleString = getSeoModuleString(seoModule);
      if (!seoModuleString) {
        $logger.error('Missing seo module');
      }
      const cacheKey = `${seoModuleString}__${id}__${$i18n.locale.value}`;
      if (_responseCache.value[cacheKey]) {
        /* pass */
      } else if (_promiseCache.value[cacheKey]) {
        await _promiseCache.value[cacheKey];
      } else {
        _promiseCache.value[cacheKey] = $api.$get(`/seo/${seoModuleString}/${id}`).catch((err) => {
          $logger.error(err);
          delete _promiseCache.value[cacheKey];
        });
        _responseCache.value[cacheKey] = await _promiseCache.value[cacheKey];
      }
      return _responseCache.value[cacheKey];
    } catch (err) {
      $logger.error(err);
    }
    return {};
  }

  function _computeSeoHeads ({ computedTitle, seo }) {
    const { $awMergedConfig, $logger } = nuxtApp;
    const {
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogType,
      ogUrl,
      ogImage,
      noIndex,
    } = {
      description: seo.description,
      keywords: seo.keywords,
      ogTitle: seo.ogTitle,
      ogDescription: seo.ogDescription,
      ogType: seo.ogType,
      ogUrl: seo.ogUrl,
      ogImage: seo.ogImage,
      noIndex: seo.noIndex,
    };
    return {
      title: `${computedTitle}`,
      __dangerouslyDisableSanitizers: ['script'],
      meta: [...(function*() {
        try {
          yield {
            hid: 'build_identifier',
            name: 'build_identifier',
            content: __AW_BUILD_IDENTIFIER__,
          };
          if (description) {
            yield {
              hid: 'description',
              name: 'description',
              content: description,
            };
          }
          if (keywords) {
            yield {
              hid: 'keywords',
              name: 'keywords',
              content: keywords,
            };
          }
          if (ogTitle) {
            yield {
              hid: 'og:title',
              name: 'og:title',
              content: ogTitle,
            };
          }
          if (ogDescription) {
            yield {
              hid: 'og:description',
              name: 'og:description',
              content: ogDescription,
            };
          }
          if (ogType) {
            yield {
              hid: 'og:type',
              name: 'og:type',
              content: ogType,
            };
          }
          if (ogUrl) {
            yield {
              hid: 'og:url',
              name: 'og:url',
              content: ogUrl,
            };
          }
          if (ogImage) {
            yield {
              hid: 'og:image',
              name: 'og:image',
              content: ogImage,
            };
            yield {
              hid: 'og:image:type',
              name: 'og:image:type',
              content: guessImgMime(ogImage) || 'image/png',
            };
          }
          if (noIndex) {
            yield {
              hid: 'robots',
              name: 'robots',
              content: 'noindex,nofollow',
            };
          }
          if ($awMergedConfig.facebookDomainVerificationId) {
            yield {
              hid: 'facebook-domain-verification',
              name: 'facebook-domain-verification',
              content: $awMergedConfig.facebookDomainVerificationId,
            };
          }
        } catch (err) {
          $logger.error(err);
        }
      })()],
    };
  }

  return {
    _responseCache,
    _seoRequestsEnRoute,
    _data,
    _titlePartPrefix,
    _titlePartMain,
    _titlePartPostfix,
    getSeoReadonlyRequestsEnRoute: () => readonly(_seoRequestsEnRoute),
    getSeoReadonlyData: () => readonly(_data),
    getSeoReadonlyTitleParts: () => readonly(computed(createSeoTitleFromStore)),
    fetchSeoData,
    getSeoData,
    getCareerPageTitle,
  };
});

function guessImgMime (url) {
  const splitted = url.split('.');
  if (splitted.length > 1) {
    return `image/${splitted.pop().replace(/jpg$/, 'jpeg')}`;

  } else {
    return '';
  }
}
